import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo as LogoImg, UserIcon, LockIcon } from '../../routes/ImagePath';
import useAuth from '../../hooks/useAuth';
import Loader from "../../components/common/GlobalLoader"

const Logo = () => (
  <div className="flex items-center justify-center gap-3 select-none w-full px-2">
    <img src={LogoImg} alt="Logo" className="h-10 sm:h-12 w-auto max-w-[min(100%,280px)] object-contain" />
  </div>
);

const InputIconUser = () => (
  <img src={UserIcon} alt="User" className="h-5 w-5" />
);

const InputIconLock = () => (
  <img src={LockIcon} alt="Lock" className="h-5 w-5" />
);

const EyeIcon = ({ open }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-gray-400">
    {open ? (
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12zm11 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
    ) : (
      <>
        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 3-3c0-.41-.08-.8-.23-1.15M9.88 4.1A10.78 10.78 0 0 1 12 4c7 0 11 8 11 8a18.86 18.86 0 0 1-4.9 5.44M6.18 6.18A18.86 18.86 0 0 0 1 12s4 8 11 8c2.02 0 3.86-.56 5.44-1.5" />
      </>
    )}
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, loading, error } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = await login(username, password);
    if (data && data.token) {
      localStorage.setItem('user', JSON.stringify(data));
      navigate('/dashboard');
    } else {
      alert(error || 'Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-center justify-center px-4 py-10 relative">
      
      {/* ✅ Global loader overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
          <Loader />
        </div>
      )}

      <div className="w-full max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-8 md:p-10">
          <div className="flex flex-col items-center text-center">
            <Logo />
            <p className="mt-4 text-sm text-gray-500">Please sign in below.</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <InputIconUser />
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                className="block w-full h-12 rounded-lg border border-gray-300 bg-white pl-11 pr-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoComplete="username"
              />
            </div>

            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <InputIconLock />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="block w-full h-12 rounded-lg border border-gray-300 bg-white pl-11 pr-10 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2 text-gray-600 select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Remember me</span>
              </label>
            </div>

            <button
              type="submit"
              className="mt-2 inline-flex w-full items-center justify-center rounded-lg theme-bg-primary px-4 py-3 font-semibold shadow-sm focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-2"
              disabled={loading}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
